import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INPUTS = [
  { key: "stress", label: "Perceived stress", hint: "0 calm — 10 very stressed", reverse: true },
  { key: "energy", label: "Energy", hint: "0 depleted — 10 energized" },
  { key: "sleep_quality", label: "Sleep quality", hint: "0 poor — 10 restorative" },
  { key: "muscle_tension", label: "Muscle tension", hint: "0 loose — 10 very tense", reverse: true },
  { key: "mental_clarity", label: "Mental clarity", hint: "0 foggy — 10 sharp" },
  { key: "emotional_regulation", label: "Emotional regulation", hint: "0 reactive — 10 steady" },
];

// v2 — optional manual biometric inputs (until a wearable bridge exists).
const BIO_INPUTS = [
  { key: "resting_hr", label: "Resting HR", unit: "bpm", min: 30, max: 120, step: 1 },
  { key: "hrv_ms", label: "HRV (RMSSD)", unit: "ms", min: 5, max: 200, step: 1 },
  { key: "respiratory_rate", label: "Respiratory rate", unit: "/min", min: 6, max: 30, step: 1 },
  { key: "sleep_hours", label: "Sleep duration", unit: "hrs", min: 0, max: 14, step: 0.5 },
  { key: "wearable_readiness", label: "Wearable readiness", unit: "%", min: 0, max: 100, step: 1 },
  { key: "post_session_hr_delta", label: "Post-session HR Δ", unit: "bpm", min: -40, max: 40, step: 1 },
  { key: "post_session_hrv_delta", label: "Post-session HRV Δ", unit: "ms", min: -100, max: 100, step: 1 },
];

function componentBarColor(v) {
  if (v >= 60) return "var(--iw-primary)";
  if (v >= 40) return "var(--iw-muted)";
  return "var(--iw-accent-red)";
}

const COMPONENT_LABELS = {
  stress: "Stress (reversed)",
  energy: "Energy",
  sleep_quality: "Sleep quality",
  muscle_tension: "Tension (reversed)",
  mental_clarity: "Mental clarity",
  emotional_regulation: "Emotional regulation",
  session_response: "Session response",
  weekly_trend: "Weekly trend",
  hrv: "HRV (biometric)",
  resting_hr: "Resting HR (biometric)",
  respiratory_rate: "Respiratory rate (biometric)",
  sleep_duration: "Sleep duration (biometric)",
  readiness: "Readiness (biometric)",
  post_hrv_delta: "Post-session HRV Δ (biometric)",
  post_hr_delta: "Post-session HR Δ (biometric)",
};

// Recovery Score — self-report composite + optional manual biometrics (v2).
// Non-diagnostic wellness metric. Requires member sign-in.
export default function RecoveryScoreCard({ collapsible = false, className = "" }) {
  const { isMember, memberKey, openLogin } = useAuth();
  const [open, setOpen] = useState(!collapsible);
  const [values, setValues] = useState(() =>
    Object.fromEntries(INPUTS.map((i) => [i.key, 5]))
  );
  const [bioOpen, setBioOpen] = useState(false);
  const [bio, setBio] = useState({});
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);

  const setBioField = (key, raw) => {
    setBio((b) => {
      const next = { ...b };
      if (raw === "" || raw === null) delete next[key];
      else next[key] = Number(raw);
      return next;
    });
  };

  const submit = async () => {
    if (!isMember || !memberKey) {
      openLogin(null);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/recovery/score`, {
        ...values,
        ...bio,
      });
      setResult(data);
      try {
        const h = await axios.get(`${API}/recovery/history/${memberKey}?limit=12`);
        setHistory(h.data.items || []);
      } catch (e) {
        setHistory([]);
      }
    } catch (e) {
      setError("Could not compute your score right now. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  let submitLabel = "Move a slider to begin";
  if (!isMember) submitLabel = "Sign in to check my score";
  else if (touched) submitLabel = "Compute my score";

  return (
    <div
      className={`iw-card p-6 sm:p-8 ${className}`}
      style={{ borderTop: "3px solid var(--iw-primary)" }}
      data-testid="recovery-score-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div
            className="iw-mono uppercase mb-1.5"
            style={{ color: "var(--iw-accent-red)", fontSize: "10px", letterSpacing: "0.22em", fontWeight: 600 }}
          >
            {result?.basis?.startsWith("Combined")
              ? "Recovery Score · Combined"
              : "Self-Reported Recovery Score"}
          </div>
          <div className="serif text-xl sm:text-2xl" style={{ color: "var(--iw-primary)" }}>
            How recovered do you feel overall?
          </div>
          <p className="text-sm mt-1.5 leading-relaxed m-0 max-w-xl" style={{ color: "var(--iw-body)" }}>
            Six quick ratings. We add your latest session response and week-over-week trend
            when available — components without data are simply left out. Wellness metric,
            non-medical.
          </p>
        </div>
        {collapsible && (
          <button
            type="button"
            className="iw-btn-ghost"
            style={{ padding: "0.65rem 1rem", fontSize: "12px" }}
            onClick={() => setOpen((v) => !v)}
            data-testid="recovery-score-toggle-btn"
          >
            {open ? "Hide" : "Check my score"}
          </button>
        )}
      </div>

      {open && !result && (
        <div className="mt-6">
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
            {INPUTS.map((inp) => (
              <div key={inp.key}>
                <div className="flex items-baseline justify-between mb-1">
                  <label
                    htmlFor={`recovery-${inp.key}`}
                    className="iw-mono uppercase"
                    style={{ color: "var(--iw-primary)", fontSize: "11px", letterSpacing: "0.16em", fontWeight: 600 }}
                  >
                    {inp.label}
                  </label>
                  <span
                    className="iw-mono"
                    style={{ color: "var(--iw-accent-red)", fontSize: "13px", fontWeight: 700 }}
                    data-testid={`recovery-${inp.key}-value`}
                  >
                    {values[inp.key]}
                  </span>
                </div>
                <input
                  id={`recovery-${inp.key}`}
                  type="range"
                  min={0}
                  max={10}
                  step={1}
                  value={values[inp.key]}
                  onChange={(e) => {
                    setTouched(true);
                    setValues((v) => ({ ...v, [inp.key]: Number(e.target.value) }));
                  }}
                  className="w-full"
                  style={{ accentColor: "var(--iw-accent-red)" }}
                  aria-label={`${inp.label} 0 to 10`}
                  data-testid={`recovery-${inp.key}-slider`}
                />
                <div className="iw-mono uppercase" style={{ color: "var(--iw-muted)", fontSize: "9px", letterSpacing: "0.14em" }}>
                  {inp.hint}
                </div>
              </div>
            ))}
          </div>

          {/* v2 — optional biometric inputs */}
          <div className="mt-6 pt-5 border-t" style={{ borderColor: "var(--iw-border-soft)" }}>
            <button
              type="button"
              onClick={() => setBioOpen((v) => !v)}
              className="iw-mono uppercase flex items-center gap-2"
              style={{ color: "var(--iw-primary)", fontSize: "11px", letterSpacing: "0.16em", fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: 0 }}
              data-testid="recovery-biometric-toggle"
            >
              <span aria-hidden="true">{bioOpen ? "−" : "+"}</span> Add wearable data (optional)
            </button>
            {bioOpen && (
              <>
                <p className="text-xs mt-2 mb-4 m-0" style={{ color: "var(--iw-muted)" }}>
                  From your wearable or ring — leave any blank. Providing these switches your basis to
                  <strong> Combined</strong>. Manual entry for now; nothing is estimated.
                </p>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
                  {BIO_INPUTS.map((b) => (
                    <div key={b.key}>
                      <label
                        htmlFor={`recovery-bio-${b.key}`}
                        className="iw-mono uppercase block mb-1"
                        style={{ color: "var(--iw-body)", fontSize: "10px", letterSpacing: "0.14em", fontWeight: 600 }}
                      >
                        {b.label} <span style={{ color: "var(--iw-muted)" }}>({b.unit})</span>
                      </label>
                      <input
                        id={`recovery-bio-${b.key}`}
                        type="number"
                        min={b.min}
                        max={b.max}
                        step={b.step}
                        value={bio[b.key] ?? ""}
                        onChange={(e) => setBioField(b.key, e.target.value)}
                        placeholder="—"
                        className="w-full px-3 py-2 bg-white outline-none text-base"
                        style={{ border: "1px solid var(--iw-border)", borderRadius: "2px", color: "var(--iw-primary)", fontFamily: "'IBM Plex Sans', sans-serif" }}
                        data-testid={`recovery-bio-${b.key}`}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {error && (
            <p className="text-sm mt-4 m-0" style={{ color: "var(--iw-accent-red)" }} data-testid="recovery-score-error">
              {error}
            </p>
          )}
          {!isMember && (
            <p className="text-sm mt-4 m-0" style={{ color: "var(--iw-muted)" }} data-testid="recovery-score-signin-hint">
              Sign in to save and track your recovery score over time.
            </p>
          )}
          <button
            type="button"
            className="iw-btn-primary mt-6"
            onClick={submit}
            disabled={busy || !touched}
            title={!touched ? "Move at least one slider first" : undefined}
            data-testid="recovery-score-submit-btn"
          >
            {busy ? "Scoring…" : submitLabel}{" "}
            <span aria-hidden="true">↗</span>
          </button>
        </div>
      )}

      {open && result && (
        <RecoveryResult result={result} history={history} onReset={() => setResult(null)} />
      )}
    </div>
  );
}

function RecoveryResult({ result, history, onReset }) {
  return (
    <div className="mt-6" data-testid="recovery-score-result">
      <div className="flex items-end gap-6 flex-wrap">
        <div>
          <div
            className="font-mono"
            style={{ color: "var(--iw-primary)", fontSize: "56px", fontWeight: 700, lineHeight: 1, fontFamily: "'Orbitron', sans-serif" }}
            data-testid="recovery-score-number"
          >
            {result.score}
            <span style={{ fontSize: "20px", opacity: 0.5 }}>/100</span>
          </div>
          <div className="iw-mono uppercase mt-1.5" style={{ color: "var(--iw-muted)", fontSize: "10px", letterSpacing: "0.20em", fontWeight: 600 }} data-testid="recovery-score-basis">
            {result.basis}
          </div>
        </div>
        {history.length > 1 && <Sparkline items={history} />}
      </div>

      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5 mt-6">
        {Object.entries(result.components).map(([k, v]) => (
          <div key={k} className="flex items-center gap-3" data-testid={`recovery-component-${k}`}>
            <span className="iw-mono uppercase flex-shrink-0 w-44" style={{ color: "var(--iw-body)", fontSize: "10px", letterSpacing: "0.14em", fontWeight: 600 }}>
              {COMPONENT_LABELS[k] || k}
            </span>
            <span className="flex-1 h-[6px]" style={{ background: "var(--iw-border-soft)", borderRadius: "2px" }}>
              <span
                className="block h-full"
                style={{
                  width: `${v}%`,
                  background: componentBarColor(v),
                  borderRadius: "2px",
                }}
              />
            </span>
            <span className="iw-mono w-8 text-right" style={{ color: "var(--iw-primary)", fontSize: "11px", fontWeight: 700 }}>
              {v}
            </span>
          </div>
        ))}
      </div>

      <p className="iw-mono uppercase mt-5 m-0" style={{ color: "var(--iw-muted)", fontSize: "9px", letterSpacing: "0.18em" }}>
        {result?.basis?.startsWith("Combined")
          ? "Saved to your wellness record · self-report + wearable data combined"
          : "Saved to your wellness record · add wearable data (HR/HRV) for a Combined score"}
      </p>
      <button
        type="button"
        className="iw-btn-ghost mt-4"
        style={{ padding: "0.6rem 1rem", fontSize: "12px" }}
        onClick={onReset}
        data-testid="recovery-score-again-btn"
      >
        Rate again
      </button>
    </div>
  );
}

function Sparkline({ items }) {
  const scores = items.map((i) => i.score);
  const w = 160;
  const h = 44;
  const min = Math.min(...scores, 0);
  const max = Math.max(...scores, 100);
  const pts = scores.map((s, i) => {
    const x = scores.length === 1 ? w / 2 : (i / (scores.length - 1)) * w;
    const y = h - ((s - min) / (max - min || 1)) * h;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const last = pts[pts.length - 1].split(",");
  return (
    <div data-testid="recovery-score-sparkline">
      <svg width={w} height={h + 8} viewBox={`0 0 ${w} ${h + 8}`} aria-hidden="true">
        <polyline points={pts.join(" ")} fill="none" stroke="var(--iw-primary)" strokeWidth="2" transform="translate(0,4)" />
        <circle cx={last[0]} cy={Number(last[1]) + 4} r="3.5" fill="var(--iw-accent-red)" />
      </svg>
      <div className="iw-mono uppercase" style={{ color: "var(--iw-muted)", fontSize: "9px", letterSpacing: "0.16em" }}>
        Last {scores.length} checks
      </div>
    </div>
  );
}
